@if (meeting$ | async; as result) {
  @if (result.isPending) {
    <app-spinner></app-spinner>
  } @else if (result.isError) {
    @if (result.error.error.message; as accessInfo) {
      @if (accessInfo && !accessInfo.hasAccess && accessInfo.sameTeam) {
        <div class="access-denied-container">
          <h2 translate="MEETINGS.ACCESS_DENIED_TITLE"></h2>
          <p class="mask-meeting-name" translate="MEETINGS.ACCESS_DENIED_MESSAGE" [translateParams]="{ meetingName: accessInfo.meetingName }"></p>

          @if (accessInfo.users.length > 0) {
            <div class="user-list-section">
              <h3 translate="MEETINGS.REQUEST_ACCESS_FROM"></h3>
              <ul class="user-list">
                @for (user of accessInfo.users; track user.id) {
                  <li>
                    <span class="user-name">{{ user.firstName }} {{ user.lastName }}</span>
                    <a href="mailto:{{ user.email }}" class="user-email">{{ user.email }}</a>
                  </li>
                }
              </ul>
              <p class="request-hint" translate="MEETINGS.REQUEST_ACCESS_HINT"></p>
            </div>
          }
        </div>
      } @else {
        <h2 class="failed-to-load" translate="COMMON.FAILED_TO_LOAD"></h2>
      }
    }
  } @else {
    @if (result.data; as meeting) {
      <div class="page-container">
        <app-toolbar [title]="meeting.name" [meetingId]="meeting.id" [backButtonLink]="constructBackButtonLink(meeting)" [showSupportButton]="true">
          <div class="actions">
            @let isRestricted = meeting.accessLevel === 'RESTRICTED';
            @if (!meeting.isUpcoming && !meeting.isOngoing && !isRestricted) {
              <button
                mat-icon-button
                [routerLink]="['/app', 'meetings', '' + meeting.id, 'setup']"
                [matTooltip]="'MEETINGS.MEETING_SETUP_BUTTON_TOOLTIP' | translate"
              >
                <mat-icon>settings</mat-icon>
              </button>
            }

            @if (meeting.transcript.length > 0 && !isRestricted) {
              <button mat-icon-button class="export" (click)="exportMeeting(meeting)" [matTooltip]="'COMMON.EXPORT' | translate">
                <mat-icon>download</mat-icon>
              </button>
            }

            @if (currentUser$ | async; as user) {
              @if (user.team.resolvedSettings.isSharingEnabled) {
                <button mat-icon-button class="share" (click)="share(meeting)" [matTooltip]="'COMMON.SHARING' | translate">
                  <mat-icon>share</mat-icon>
                </button>
              }
              @if (user.role === 'SUPPORT') {
                <button mat-icon-button class="timeline" (click)="showTimeline(meeting)" [matTooltip]="'MEETINGS.TIMELINE.BUTTON_TOOLTIP' | translate">
                  <mat-icon fontSet="material-symbols-outlined">schedule</mat-icon>
                </button>
              }
            }
            @if (!meeting.isUpcoming && !meeting.isOngoing && !isRestricted) {
              <button mat-icon-button class="info" (click)="toggleInfoPanel()" [matTooltip]="'MEETINGS.MEETING_INFO.TITLE' | translate">
                <mat-icon>info_outline</mat-icon>
              </button>
            }

            @if ((currentUser$ | async)?.isSensitiveModeEnabled) {
              @let isSensitiveModeEnabled = (isSensitiveModeEnabled$ | async) ?? false;
              <button
                mat-icon-button
                class="sensitive-mode-toggle"
                [class.active]="isSensitiveModeEnabled"
                (click)="toggleSensitiveMode()"
                [matTooltip]="(isSensitiveModeEnabled ? 'MEETINGS.SENSITIVE_MODE.DISABLE' : 'MEETINGS.SENSITIVE_MODE.ENABLE') | translate"
              >
                <mat-icon>{{ isSensitiveModeEnabled ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
            }
          </div>
          <app-meeting-details-bar class="details" [meeting]="meeting" [templateItems]="(templateItems$ | async) ?? []"></app-meeting-details-bar>
        </app-toolbar>
        @if (meeting.status === 'WAITING_FOR_RETRY') {
          <app-banner [message]="'MEETINGS.RESEND_ASSISTANT.ON_ITS_WAY' | translate"></app-banner>
        }
        <div class="page">
          <div class="wrapper">
            <mat-drawer-container class="drawer-container">
              <mat-drawer class="sidenav" mode="over" position="end" [opened]="isInfoPanelOpen$ | async" (closed)="meetingInfo.resetEditState()">
                <app-meeting-info #meetingInfo [meeting]="meeting"></app-meeting-info>
                <div class="close-icon">
                  <button mat-icon-button (click)="toggleInfoPanel()">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </mat-drawer>
              @let editorMaximized = (isEditorMaximized$ | async) ?? false;
              <!-- A Restricted user reads only their minutes: no meeting recording, transcript or papers beside them. -->
              @let isMinutesOnly = meeting.accessLevel === 'RESTRICTED';
              <div class="main-content">
                <as-split [direction]="useVerticalMode ? 'vertical' : 'horizontal'" [gutterSize]="0">
                  <as-split-area [size]="editorMaximized || isMinutesOnly ? 100 : editorSize" [lockSize]="true">
                    <div class="left-side-container">
                      <div class="outputs">
                        <app-meeting-outputs-container
                          [meeting]="meeting"
                          [canUserEdit]="canUserEdit(meeting)"
                          [canUserSuggest]="canUserSuggest(meeting)"
                          [canUserComment]="canUserComment(meeting)"
                          [canResolveAnyThread]="canResolveAnyThread(meeting)"
                          [isSensitiveModeEnabled]="(isSensitiveModeEnabled$ | async) ?? false"
                          (openAgendaUpload)="meetingResourcesContainer?.handleAgendaTabClick()"
                          (openDocument)="meetingResourcesContainer?.handleOpenDocumentEvent($event)"
                          (openMeeting)="meetingResourcesContainer?.showCitedSentences($event)"
                        ></app-meeting-outputs-container>
                      </div>
                    </div>
                  </as-split-area>
                  @if (!isMinutesOnly) {
                    <as-split-area [size]="100 - editorSize" [lockSize]="true" [visible]="!editorMaximized">
                      <div class="right-side-container">
                        <app-meeting-resources-container
                          [canEdit]="canUserEdit(meeting)"
                          [userHasEditorAccess]="userHasEditorAccess(meeting)"
                          [meeting]="meeting"
                          [isSensitiveModeEnabled]="(isSensitiveModeEnabled$ | async) ?? false"
                        ></app-meeting-resources-container>
                      </div>
                    </as-split-area>
                  }
                </as-split>
              </div>
            </mat-drawer-container>
          </div>
        </div>
      </div>
    }
  }
}
